// The config dialog's schedule, as the operator sets it, and the translation to and from the rows
// that store it. Pure — no React, no GraphQL — so every case can be tested against core's own
// scheduler.
//
//   { type, startKey, days, monthDays, dates }
//
//   type       WEEKLY | BIWEEKLY | MONTHLY | ONE_OFF
//   startKey   'YYYY-MM-DD', the first day the schedule can run
//   days       { sunday: bool, … }   WEEKLY and BIWEEKLY
//   monthDays  [1, 15, …]            MONTHLY
//   dates      ['YYYY-MM-DD', …]     ONE_OFF
//
// Every date is a key, compared as a string. Rules start at the key's UTC midnight, which is the
// convention rulesFromDays already writes and core reads back with its UTC getters (170.0.0).
// One-off days are overrides spanning the business day, the same bounds a lock is written with.

import { calendarDate, parseDateKey, shiftDateKey } from '@onward-delivery/design/calendar-utils';
import { DAY_NAMES, EMPTY_DAYS, notEnded, rulesFromDays, sameDays } from '@/utilities/resourceDays';
import { coreDay } from '../../utils';

export const SCHEDULE_TYPES = Object.freeze({
    WEEKLY: 'WEEKLY',
    BIWEEKLY: 'BIWEEKLY',
    MONTHLY: 'MONTHLY',
    ONE_OFF: 'ONE_OFF',
});

export const SCHEDULE_OPTIONS = [
    { value: SCHEDULE_TYPES.WEEKLY, label: 'Weekly' },
    { value: SCHEDULE_TYPES.BIWEEKLY, label: 'Biweekly' },
    { value: SCHEDULE_TYPES.MONTHLY, label: 'Monthly' },
    { value: SCHEDULE_TYPES.ONE_OFF, label: 'Does not repeat' },
];

// Which schedule a stored rule belongs to. DAILY and WEEKDAYS are what rulesFromDays collapses a
// weekly pick into, so they read back as Weekly.
const TYPE_OF_RULE = {
    DAILY: SCHEDULE_TYPES.WEEKLY,
    WEEKDAYS: SCHEDULE_TYPES.WEEKLY,
    WEEKLY: SCHEDULE_TYPES.WEEKLY,
    BIWEEKLY: SCHEDULE_TYPES.BIWEEKLY,
    MONTHLY: SCHEDULE_TYPES.MONTHLY,
};

// ---- Date keys --------------------------------------------------------------------------------

const DAY_MS = 86400000;

// A stored instant's date, read in UTC. Rules are written at UTC midnight and one-offs at business
// midnight, and every US business midnight still falls on the same UTC date.
export const keyOf = (instant) => new Date(instant).toISOString().slice(0, 10);

export const utcMidnightOf = (key) => calendarDate(key).toISOString();

const dowOf = (key) => calendarDate(key).getUTCDay();

const dayOfMonth = (key) => parseDateKey(key).day;

export const earliest = (keys) => [...keys].sort()[0];

// The first date on or after `key` that falls on weekday `dow`.
const nextOnDow = (key, dow) => shiftDateKey(key, (dow - dowOf(key) + 7) % 7);

// Core's BIWEEKLY runs in ISO weeks (Monday first), counted in UTC. Same arithmetic, so the weeks
// the dialog anchors to are the weeks core counts.
const isoWeekIndex = (key) => Math.floor((Math.floor(calendarDate(key).getTime() / DAY_MS) + 3) / 7);

// ---- Reading a saved resource -----------------------------------------------------------------

export const emptySchedule = (startKey) => ({
    type: SCHEDULE_TYPES.WEEKLY,
    startKey,
    days: { ...EMPTY_DAYS },
    monthDays: [],
    dates: [],
});

// Extra working days: omit:false rows no rule owns. A lock is omit:true, and a one-off never
// carries rule_id — so this can't pick up either.
export const upcomingOneOffs = (resource, todayStart) =>
    (resource?.overrides || []).filter(
        (override) =>
            override.omit === false && !override.rule_id && new Date(override.override_end) > new Date(todayStart)
    );

// Everything still scheduling a day from today on: live rules and ones yet to start.
export const scheduledRules = (resource, todayStart) =>
    (resource?.rules || []).filter((rule) => notEnded(rule, new Date(todayStart)));

// What the dialog opens on. `mixed` flags a schedule one form can't show — weekly and monthly
// rules together, say, or biweekly weekdays on different weeks — so the dialog can say saving
// replaces it. Untouched, it still writes nothing.
export const scheduleFromResource = (resource, today) => {
    const rules = scheduledRules(resource, today.start);

    if (rules.length === 0) {
        const dates = [...new Set(upcomingOneOffs(resource, today.start).map((o) => keyOf(o.override_start)))].sort();

        return dates.length
            ? { ...emptySchedule(dates[0]), type: SCHEDULE_TYPES.ONE_OFF, dates, mixed: false }
            : { ...emptySchedule(today.key), mixed: false };
    }

    const types = [...new Set(rules.map((rule) => TYPE_OF_RULE[rule.rule_type]).filter(Boolean))];
    const type = SCHEDULE_OPTIONS.map(({ value }) => value).find((value) => types.includes(value));
    const own = rules.filter((rule) => TYPE_OF_RULE[rule.rule_type] === type);
    const starts = own.map((rule) => keyOf(rule.rule_start));

    const schedule = { ...emptySchedule(earliest(starts)), type };

    if (type === SCHEDULE_TYPES.WEEKLY) {
        own.forEach((rule) => {
            if (rule.rule_type === 'DAILY') DAY_NAMES.forEach((day) => (schedule.days[day] = true));
            else if (rule.rule_type === 'WEEKDAYS') DAY_NAMES.slice(1, 6).forEach((day) => (schedule.days[day] = true));
            else schedule.days[DAY_NAMES[dowOf(keyOf(rule.rule_start))]] = true;
        });
    }

    if (type === SCHEDULE_TYPES.BIWEEKLY) {
        starts.forEach((key) => (schedule.days[DAY_NAMES[dowOf(key)]] = true));
    }

    if (type === SCHEDULE_TYPES.MONTHLY) {
        schedule.monthDays = [...new Set(starts.map(dayOfMonth))].sort((a, b) => a - b);
    }

    const offWeek = type === SCHEDULE_TYPES.BIWEEKLY && new Set(starts.map((key) => isoWeekIndex(key) % 2)).size > 1;

    return { ...schedule, mixed: types.length > 1 || offWeek };
};

// ---- Comparing -------------------------------------------------------------------------------

const sameList = (a = [], b = []) => {
    const sortedB = [...b].sort();
    return a.length === b.length && [...a].sort().every((v, i) => v === sortedB[i]);
};

// Only the parts the schedule's type uses, so switching type and back reads as unchanged.
export const sameSchedule = (a, b) => {
    if (!a || !b || a.type !== b.type || a.startKey !== b.startKey) return false;

    switch (a.type) {
        case SCHEDULE_TYPES.MONTHLY:
            return sameList(a.monthDays, b.monthDays);
        case SCHEDULE_TYPES.ONE_OFF:
            return sameList(a.dates, b.dates);
        default:
            return sameDays(a.days, b.days);
    }
};

// ---- Editing ---------------------------------------------------------------------------------

// The schedule with its Recurring dropdown moved to `type`. A one-off schedule starts on its first
// date, so entering one parks the repeating start date and leaving one puts it back — otherwise a
// look at "Does not repeat" reset the start to today and a save the operator never meant rewrote
// the schedule. `todayKey` covers a schedule that was one-off to begin with.
export const withType = (schedule, type, todayKey) => {
    if (type === schedule.type) return schedule;

    if (type === SCHEDULE_TYPES.ONE_OFF) {
        return { ...schedule, type, repeatingStartKey: schedule.startKey, startKey: schedule.dates[0] || todayKey };
    }

    const { repeatingStartKey, ...rest } = schedule;
    const startKey = schedule.type === SCHEDULE_TYPES.ONE_OFF ? repeatingStartKey || todayKey : schedule.startKey;

    return { ...rest, type, startKey };
};

// ---- Validating ------------------------------------------------------------------------------

const pickedDays = (schedule) => DAY_NAMES.some((day) => schedule?.days?.[day]);

// What stops Save, as the message to show. `isNew` because only a new resource is held to today:
// an existing one can be moved into the past, which the Save step then limits to the whole-schedule option.
export const scheduleError = (schedule, { isNew, todayKey }) => {
    if (!schedule?.startKey) return 'Pick a start date.';
    if (isNew && schedule.startKey < todayKey) return 'A new resource can’t start in the past.';

    switch (schedule.type) {
        case SCHEDULE_TYPES.MONTHLY:
            return schedule.monthDays?.length ? false : 'Pick at least one day of the month.';
        case SCHEDULE_TYPES.ONE_OFF:
            return schedule.dates?.length ? false : 'Pick at least one date.';
        default:
            return pickedDays(schedule) ? false : 'Pick at least one day.';
    }
};

// Months without the 29th, 30th or 31st are skipped by core's MONTHLY, so the dialog says so.
export const skipsShortMonths = (schedule) =>
    schedule?.type === SCHEDULE_TYPES.MONTHLY && (schedule.monthDays || []).some((day) => day > 28);

// ---- Writing ---------------------------------------------------------------------------------

// Every biweekly rule runs the same weeks: the anchor's week and every second week after. The
// first run is the weekday's first date on or after `fromKey` that lands on one of those weeks.
//
// `anchorKey` and `fromKey` differ on a "This and following" save, which starts the new rules from
// today but has to keep the weeks the schedule already runs on. Anchoring to today instead flipped
// the schedule onto its off weeks whenever it was saved during one.
const biweeklyStartKey = (fromKey, dow, anchorKey) => {
    const first = nextOnDow(fromKey, dow);
    return (isoWeekIndex(first) - isoWeekIndex(anchorKey)) % 2 === 0 ? first : shiftDateKey(first, 7);
};

// The first date on or after the start that falls on the `day`th of its month. A 31st started in
// September lands on Oct 31, since September has none.
const monthlyStartKey = (startKey, day) => {
    const year = Number(startKey.slice(0, 4));
    const month = Number(startKey.slice(5, 7)) - 1;

    for (let ahead = 0; ahead < 13; ahead += 1) {
        const candidate = new Date(Date.UTC(year, month + ahead, day));
        if (candidate.getUTCDate() === day && keyOf(candidate) >= startKey) return keyOf(candidate);
    }

    return null;
};

// The rule rows a schedule stores as, running from `fromKey` on. ONE_OFF has none — its days are
// overrides. `fromKey` is later than the start date only on a "This and following" save that
// switches over today; biweekly still counts its weeks from the start date.
export const rulesForSchedule = (schedule, fromKey = schedule.startKey) => {
    const weekdays = DAY_NAMES.filter((day) => schedule.days?.[day]);

    switch (schedule.type) {
        case SCHEDULE_TYPES.WEEKLY:
            return rulesFromDays(schedule.days, utcMidnightOf(fromKey), { forward: true });
        case SCHEDULE_TYPES.BIWEEKLY:
            return weekdays.map((day) => ({
                rule_start: utcMidnightOf(biweeklyStartKey(fromKey, DAY_NAMES.indexOf(day), schedule.startKey)),
                rule_type: 'BIWEEKLY',
            }));
        case SCHEDULE_TYPES.MONTHLY:
            return [...(schedule.monthDays || [])]
                .sort((a, b) => a - b)
                .map((day) => monthlyStartKey(fromKey, day))
                .filter(Boolean)
                .map((key) => ({ rule_start: utcMidnightOf(key), rule_type: 'MONTHLY' }));
        default:
            return [];
    }
};

// The first day the schedule actually runs, which is what the start date reads back as once saved:
// a rule has to start on a day it runs, so a Tuesday start with Mon/Wed picked is stored from the
// Wednesday. Null when nothing is picked, or there's no start date yet.
export const firstRunKey = (schedule) => {
    if (schedule.type === SCHEDULE_TYPES.ONE_OFF) return earliest(schedule.dates) || null;
    if (!schedule.startKey) return null;

    const runs = rulesForSchedule(schedule).map((rule) => {
        const key = keyOf(rule.rule_start);
        // WEEKDAYS starts where the pick does, which can be a weekend.
        if (rule.rule_type === 'WEEKDAYS' && [0, 6].includes(dowOf(key))) return nextOnDow(key, 1);
        return key;
    });

    return earliest(runs) || null;
};

// One-off days as overrides spanning each business day — omit:false, owned by no rule.
export const oneOffRows = (dates, tz) =>
    [...dates].sort().map((key) => {
        const { start, end } = coreDay(key, tz);
        return { override_start: start, override_end: end, omit: false };
    });

// Two rules that schedule the same days, whenever each was written. Lets a SPLIT keep a rule that
// hasn't started yet instead of deleting and re-adding it.
export const recurrenceKey = (rule) => {
    const key = keyOf(rule.rule_start);

    switch (rule.rule_type) {
        case 'WEEKLY':
            return `WEEKLY:${dowOf(key)}`;
        case 'BIWEEKLY':
            return `BIWEEKLY:${dowOf(key)}:${isoWeekIndex(key) % 2}`;
        case 'MONTHLY':
            return `MONTHLY:${dayOfMonth(key)}`;
        default:
            return rule.rule_type;
    }
};
